import React, { useEffect, useRef } from 'react';
import { useTheme } from '@principal-ade/industry-theme';

interface CommandInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onClose: () => void;
  onEnter?: () => void;
}

/**
 * CommandInput - Input field for the command palette
 * Auto-focuses on mount and handles input changes
 */
export const CommandInput: React.FC<CommandInputProps> = ({
  value,
  onChange,
  placeholder = '> Type a command...',
  onClose,
  onEnter,
}) => {
  const { theme } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        onEnter?.();
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  return (
    <div
      style={{
        padding: '0',
        borderBottom: `1px solid ${theme.colors.border}`,
        backgroundColor: theme.colors.background,
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '16px 20px',
          fontSize: '16px',
          fontFamily: theme.fonts.monospace,
          backgroundColor: 'transparent',
          border: 'none',
          outline: 'none',
          color: theme.colors.text,
          caretColor: theme.colors.primary,
        }}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />
    </div>
  );
};
